import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

const initialErrors = {
  code: '',
  description: '',
  type: '',
  status: '',
  validityPeriodDays: '',
  applicationLimit: '',
  discountPercentage: '',
  applicableZones: '',
};

export default function CreatePromoCodeModal({
  open,
  onClose,
  onCreate,
  isCreating,
}) {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [validityPeriodDays, setValidityPeriodDays] = useState('');
  const [applicationLimit, setApplicationLimit] = useState('');
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [applicableZonesText, setApplicableZonesText] = useState('');
  const [errors, setErrors] = useState(initialErrors);

  useEffect(() => {
    if (!open) {
      setCode('');
      setDescription('');
      setType('');
      setStatus('ACTIVE');
      setValidityPeriodDays('');
      setApplicationLimit('');
      setDiscountPercentage('');
      setApplicableZonesText('');
      setErrors(initialErrors);
    }
  }, [open]);

  const validateAndBuildPayload = () => {
    const nextErrors = { ...initialErrors };
    let valid = true;

    if (!code || typeof code !== 'string' || code.trim().length === 0) {
      nextErrors.code = 'Code name is required.';
      valid = false;
    }

    if (!description || typeof description !== 'string' || description.trim().length === 0) {
      nextErrors.description = 'Description is required.';
      valid = false;
    } else if (description.trim().length > 40) {
      nextErrors.description = 'Description must be 40 characters or less.';
      valid = false;
    }

    if (!type || typeof type !== 'string' || type.trim().length === 0) {
      nextErrors.type = 'Type is required.';
      valid = false;
    }

    if (!status || !['ACTIVE', 'INACTIVE', 'EXPIRED'].includes(status)) {
      nextErrors.status = 'Status must be ACTIVE, INACTIVE, or EXPIRED.';
      valid = false;
    }

    const numericValidity = Number(validityPeriodDays);
    if (!validityPeriodDays
      || Number.isNaN(numericValidity)
      || !Number.isInteger(numericValidity) || numericValidity <= 0) {
      nextErrors.validityPeriodDays = 'Validity period days must be a positive integer.';
      valid = false;
    }

    const numericApplicationLimit = Number(applicationLimit);
    if (!applicationLimit || Number.isNaN(numericApplicationLimit)
      || !Number.isInteger(numericApplicationLimit) || numericApplicationLimit <= 0) {
      nextErrors.applicationLimit = 'Application limit must be a positive integer.';
      valid = false;
    }

    const numericDiscount = Number(discountPercentage);
    if (
      discountPercentage === ''
      || Number.isNaN(numericDiscount)
      || !Number.isInteger(numericDiscount)
      || numericDiscount < 0
      || numericDiscount > 100
    ) {
      nextErrors.discountPercentage = 'Discount percentage must be an integer between 0 and 100.';
      valid = false;
    }

    const zoneIdArray = applicableZonesText
      .split(',')
      .map((z) => z.trim())
      .filter((z) => z.length > 0);

    if (zoneIdArray.length === 0) {
      nextErrors.applicableZones = 'At least one zone must be provided.';
      valid = false;
    }

    setErrors(nextErrors);

    if (!valid) {
      return null;
    }

    const payload = {
      code: code.trim(),
      description: description.trim(),
      type: type.trim(),
      status,
      validityPeriodDays: numericValidity,
      applicationLimit: numericApplicationLimit,
      discountPercentage: numericDiscount,
      applicableZones: zoneIdArray,
    };

    return payload;
  };

  const handleSubmit = () => {
    const payload = validateAndBuildPayload();
    if (!payload) {
      return;
    }
    onCreate(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Create Promo Code</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Define the properties of the new promo code. All fields are required.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Code Name"
              margin="dense"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              error={Boolean(errors.code)}
              helperText={errors.code || 'Unique promo code identifier'}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Status"
              margin="dense"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              error={Boolean(errors.status)}
              helperText={errors.status}
              required
            >
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              <MenuItem value="EXPIRED">EXPIRED</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              margin="dense"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={Boolean(errors.description)}
              helperText={errors.description || 'Max 40 characters'}
              required
              inputProps={{ maxLength: 40 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Type"
              margin="dense"
              value={type}
              onChange={(e) => setType(e.target.value)}
              error={Boolean(errors.type)}
              helperText={errors.type || 'e.g., First-time user, Campaign XYZ'}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Validity Period (Days)"
              type="number"
              margin="dense"
              value={validityPeriodDays}
              onChange={(e) => setValidityPeriodDays(e.target.value)}
              error={Boolean(errors.validityPeriodDays)}
              helperText={errors.validityPeriodDays}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Application Limit"
              type="number"
              margin="dense"
              value={applicationLimit}
              onChange={(e) => setApplicationLimit(e.target.value)}
              error={Boolean(errors.applicationLimit)}
              helperText={errors.applicationLimit || 'Maximum number of times this code can be used'}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Discount Percentage (%)"
              type="number"
              margin="dense"
              value={discountPercentage}
              onChange={(e) => setDiscountPercentage(e.target.value)}
              error={Boolean(errors.discountPercentage)}
              helperText={errors.discountPercentage || '0-100'}
              required
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Applicable Zones"
              margin="dense"
              value={applicableZonesText}
              onChange={(e) => setApplicableZonesText(e.target.value)}
              error={Boolean(errors.applicableZones)}
              helperText={errors.applicableZones || 'Comma-separated zone IDs. Example: ZONE_A, ZONE_B'}
              required
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isCreating}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isCreating}>
          {isCreating ? 'Creating…' : 'Create Promo Code'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
